import React, { useCallback, useEffect, useRef, useState } from 'react';
import ContentEditable from '../utils/SafeContentEditable';
import { createBlock } from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { applyInlineMarkdown, handleEditablePaste } from '../utils/pasteUtils';
import {
  getCaretSplit,
  isCaretAtStart,
  isCaretAtEnd,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../utils/caretUtils';
import { consumeListStartBackspace, handleIndentKeyDown, indentStyle, inheritIndent, shouldHandleLineStartBackspace, startPropFromTyped } from '../utils/indentUtils';
import { useMathPills } from '../hooks/useMathPills';
import { useVariableRefresh } from '../hooks/useVariableRefresh';
import { useVariablePicker } from '../hooks/useVariablePicker';
import { tryDeleteAdjacentAtomic } from '../utils/atomicCaret';
import {
  beginCheckboxPaint,
  consumeCheckboxPaintClick,
  endCheckboxPaint,
  registerCheckboxPainter,
  takeCheckboxPaintHistorySlot,
} from '../utils/checkboxPaint';
import {
  CHECKBOX_HOLD_MS,
  nextCheckboxTick,
  readDisplayedTick,
  TICK_INDETERMINATE,
  tickToProps,
} from '../utils/tickOverrides';

/**
 * CheckboxBlock — a step you can tick off.
 *
 * Enter chains the next checkbox (splitting mid-text carries the remainder
 * into it); Enter on an EMPTY checkbox exits the list into a paragraph.
 * Backspace at the start (or when empty) outdents, then drops the checkbox,
 * then leaves a paragraph — text is never lost. Arrow keys walk into
 * neighbouring blocks.
 *
 * Click toggles empty ↔ checked. Hold still ~500ms (no movement) for a
 * grey partial tick; click that to check, hold again to clear. Drag after
 * pressing — even if a partial already previewed — paints that click-toggle
 * onto every checkbox in the drag direction. Uncheck-all clears partials too.
 *
 * IMPORTANT (Template vs Run): the block's stable id is what a mission's
 * run-state will key its ticks on later (Chunk 11b). Ticking here edits the
 * TEMPLATE (used while authoring); in Run mode ticks will be redirected to
 * the mission and never written onto the Doc.
 */
export const CheckboxBlock = ({
  block,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onNavigate,
  onMergeWithPrevious,
  onMergeWithNext,
  tickOverrides,
  onTickChange,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const displayedTick = readDisplayedTick(block, tickOverrides);
  const [content, setContent] = useState(block.content || '');
  const [tick, setTick] = useState(displayedTick);
  const contentEditableRef = useRef(null);
  const contentRef = useRef(content);
  const tickRef = useRef(displayedTick);
  const holdTimerRef = useRef(null);
  const holdFiredRef = useRef(false);
  const gestureOriginTickRef = useRef(displayedTick);
  tickRef.current = tick;

  const checked = tick === true;
  const indeterminate = tick === TICK_INDETERMINATE;

  // Sync external tree changes (e.g. a merge writing into this block)
  useEffect(() => {
    if ((block.content || '') !== contentRef.current) {
      contentRef.current = block.content || '';
      setContent(block.content || '');
    }
  }, [block.content]);

  // Sync external tick changes (e.g. "Uncheck all", per-mission overrides)
  useEffect(() => {
    tickRef.current = displayedTick;
    setTick(displayedTick);
  }, [displayedTick, block.id, tickOverrides, block.props?.checked, block.props?.indeterminate]);

  useEffect(
    () => () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    },
    []
  );

  // Variable pills show the CURRENT context's values
  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  const { tryOpenFromBraceKey, variablePicker } = useVariablePicker({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const { mathMenu } = useMathPills({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const handleChange = (e) => {
    const value = applyInlineMarkdown(e.target.value);
    const typed = stripHtml(value).replace(/\u00a0/g, ' ');
    const prevTyped = stripHtml(contentRef.current).replace(/\u00a0/g, ' ');

    // "- " at the start converts this checkbox into a bullet (space required)
    if (/^- /.test(typed) && !/^- /.test(prevTyped)) {
      const remainder = value.replace(/^-(&nbsp;|\u00a0| )/, '');
      onUpdate(block.id, { type: 'bullet', content: remainder, props: inheritIndent(block) });
      focusEditable(block.id, { atStart: true });
      return;
    }

    if (/^\d+\. /.test(typed) && !/^\d+\. /.test(prevTyped)) {
      const remainder = value.replace(/^\d+\.(&nbsp;|\u00a0| )/, '');
      onUpdate(block.id, {
        type: 'number',
        content: remainder,
        props: { ...inheritIndent(block), ...startPropFromTyped(typed) },
      });
      focusEditable(block.id, { atStart: true });
      return;
    }

    contentRef.current = value;
    setContent(value);
    onUpdate(block.id, { content: value });
  };

  const applyTick = useCallback(
    (nextTick, { fromPaint = false } = {}) => {
      tickRef.current = nextTick;
      setTick(nextTick);
      const record = !fromPaint || takeCheckboxPaintHistorySlot();
      if (onTickChange) {
        onTickChange(block.id, nextTick);
      } else {
        onUpdate(
          block.id,
          { props: { ...block.props, ...tickToProps(nextTick) } },
          { record }
        );
      }
    },
    [block.id, block.props, onTickChange, onUpdate]
  );

  const applyTickRef = useRef(applyTick);
  applyTickRef.current = applyTick;

  useEffect(
    () =>
      registerCheckboxPainter(block.id, {
        getTick: () => tickRef.current,
        applyTick: (next, opts) => applyTickRef.current(next, opts),
      }),
    [block.id]
  );

  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const revertHoldPreview = () => {
    if (!holdFiredRef.current) return;
    holdFiredRef.current = false;
    const restore = gestureOriginTickRef.current;
    tickRef.current = restore;
    setTick(restore);
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    holdFiredRef.current = false;
    gestureOriginTickRef.current = tickRef.current;
    clearHoldTimer();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom / older hosts */
    }
    beginCheckboxPaint({
      originId: block.id,
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      editorRoot: e.currentTarget.closest('.doc-editor'),
      onLeaveStill: () => {
        clearHoldTimer();
        revertHoldPreview();
      },
      onActivate: () => {
        clearHoldTimer();
        revertHoldPreview();
      },
    });
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      holdFiredRef.current = true;
      // Preview only — persist on pointer-up if they never moved.
      const next = nextCheckboxTick(gestureOriginTickRef.current, 'hold');
      tickRef.current = next;
      setTick(next);
    }, CHECKBOX_HOLD_MS);
  };

  const handlePointerUp = (e) => {
    clearHoldTimer();
    const painted = endCheckboxPaint(e.pointerId);
    if (painted) {
      holdFiredRef.current = false;
    } else if (holdFiredRef.current) {
      applyTick(tickRef.current);
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released or unsupported */
    }
  };

  const handlePointerCancel = (e) => {
    clearHoldTimer();
    holdFiredRef.current = false;
    endCheckboxPaint(e.pointerId);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released or unsupported */
    }
  };

  const handleClick = () => {
    if (holdFiredRef.current) {
      holdFiredRef.current = false;
      return;
    }
    if (consumeCheckboxPaintClick()) return;
    applyTick(nextCheckboxTick(tickRef.current, 'click'));
  };

  const handleKeyDown = (e) => {
    const el = contentEditableRef.current;
    const isEmpty = stripHtml(contentRef.current).trim() === '';

    if (handleIndentKeyDown(e, block, onUpdate, el)) return;
    if (tryOpenFromBraceKey(e)) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isEmpty) {
        // Enter on an empty checkbox exits the checklist into a paragraph
        onUpdate(block.id, { type: 'text', content: '', props: {} });
        focusEditable(block.id);
        return;
      }
      const split = el ? getCaretSplit(el) : null;
      if (split && stripHtml(split.after).length > 0) {
        contentRef.current = split.before;
        setContent(split.before);
        onUpdateAndInsert(
          block.id,
          { content: split.before },
          createBlock('checkbox', split.after, { checked: false, ...inheritIndent(block) }),
          { focusAtStart: true }
        );
      } else {
        onInsertAfter?.(
          block.id,
          createBlock('checkbox', '', { checked: false, ...inheritIndent(block) })
        );
      }
      return;
    }

    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(block.id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(block.id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(block.id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(block.id, 'right')) e.preventDefault();
      return;
    }

    if (
      shouldHandleLineStartBackspace(e, el, { isEmpty, blockId: block.id }) &&
      consumeListStartBackspace(e, block, {
        content: contentRef.current || '',
        onUpdate,
        onMergeWithPrevious,
      })
    ) {
      return;
    }

    if (tryDeleteAdjacentAtomic(e)) {
      if (el) {
        const value = el.innerHTML;
        contentRef.current = value;
        setContent(value);
        onUpdate(block.id, { content: value });
      }
      return;
    }

    // Delete at end: pull the next block's text up onto this checkbox
    if (e.key === 'Delete' && el && isCaretAtEnd(el)) {
      // Prefer live DOM so we don't drop unflushed keystrokes on merge
      const live = el.innerHTML;
      contentRef.current = live;
      setContent(live);
      if (onMergeWithNext?.(block.id, live)) {
        e.preventDefault();
        return;
      }
    }
  };

  return (
    <>
      <div className="checkbox-block" style={indentStyle(block)}>
        <button
          type="button"
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : checked}
          aria-label={checked ? 'Mark incomplete' : 'Mark complete'}
          data-checkbox-id={block.id}
          title="Click to toggle · drag to check many · hold still for partial"
          className={`checkbox-toggle${
            checked ? ' checkbox-toggle--checked' : ''
          }${indeterminate ? ' checkbox-toggle--indeterminate' : ''}`}
          onClick={handleClick}
          onMouseDown={(e) => e.preventDefault()}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          {checked && (
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M2.5 6.2 5 8.7 9.5 3.8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          )}
          {indeterminate && (
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M2.5 6h7"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          )}
        </button>
        <ContentEditable
          innerRef={contentEditableRef}
          html={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handleEditablePaste}
          className={`checkbox-content doc-editable ${checked ? 'checked' : ''}`}
          tagName="p"
          placeholder="To-do…"
        />
      </div>
      {variablePicker}
      {mathMenu}
    </>
  );
};
